import { api } from '@/api/repository/axios';
import type { SubwayArea, SubwayStation } from '@/api/repository/types/types';
import { AREA_ALL_PREFIX } from './recruitmentListQuery';

/**
 * 주소의 역 id → 이름·노선(칩 글자용). 역 id 는 `{노선id}_{역번호}` 라 노선 역 목록 한 번으로 이름을 찾는다.
 * 옛 링크 값 `{노선id}_0`(노선 전체)은 "서울 2호선 전체", `AREA_n`(지역 전체)은 "수도권 전체"로 보인다.
 * 한 번 찾은 이름은 기억해 둔다(역 검색에서 고른 역은 [rememberStations] 로 바로 넣는다).
 */

const known: Map<string, SubwayStation> = new Map();
const lineRequests: Map<string, Promise<SubwayStation[]>> = new Map();
let areaRequest: Promise<SubwayArea[]> | null = null;

export const rememberStations = (stations: SubwayStation[]): void => {
    stations.forEach((station: SubwayStation) => known.set(station.id, station));
};

export const knownStation = (id: string): SubwayStation | undefined => known.get(id);

const lineIdOf = (id: string): string => {
    if (id.endsWith('_0')) return id.slice(0, -2);
    const at: number = id.lastIndexOf('_');
    return at > 0 ? id.slice(0, at) : '';
};

const lineStations = (lineId: string): Promise<SubwayStation[]> => {
    let request: Promise<SubwayStation[]> | undefined = lineRequests.get(lineId);
    if (!request) {
        request = api.search.getSubwayStations(lineId)
            .then((response) => (Array.isArray(response?.result) ? (response.result as SubwayStation[]) : []))
            .catch(() => {
                lineRequests.delete(lineId);
                return [];
            });
        lineRequests.set(lineId, request);
    }
    return request;
};

const areas = (): Promise<SubwayArea[]> => {
    if (!areaRequest) {
        areaRequest = api.search.getSubwayArea()
            .then((response) => (Array.isArray(response?.result) ? (response.result as SubwayArea[]) : []))
            .catch(() => {
                areaRequest = null;
                return [];
            });
    }
    return areaRequest;
};

/** 모르는 역 id 의 이름을 찾아 기억한다 — 찾은 역 목록(요청 순서)을 돌려준다 */
export const resolveStations = async (ids: string[]): Promise<SubwayStation[]> => {
    const missing: string[] = ids.filter((id: string) => !known.has(id));
    if (missing.length > 0) {
        const areaIds: string[] = missing.filter((id: string) => id.startsWith(AREA_ALL_PREFIX));
        if (areaIds.length > 0) {
            const areaList: SubwayArea[] = await areas();
            areaIds.forEach((id: string) => {
                const area: SubwayArea | undefined = areaList.find((item: SubwayArea) => String(item.id) === id.slice(AREA_ALL_PREFIX.length));
                if (area) known.set(id, { id, name: `${area.name} 전체`, lineNo: '' });
            });
        }
        const lineIds: string[] = Array.from(new Set(missing.filter((id: string) => !id.startsWith(AREA_ALL_PREFIX)).map(lineIdOf).filter(Boolean)));
        const lists: SubwayStation[][] = await Promise.all(lineIds.map(lineStations));
        lists.forEach((list: SubwayStation[], index: number) => {
            list.forEach((station: SubwayStation) => { if (!known.has(station.id)) known.set(station.id, station); });
            const lineAll: string = `${lineIds[index]}_0`;
            if (missing.includes(lineAll) && list[0]) known.set(lineAll, { id: lineAll, name: `${list[0].lineNo} 전체`, lineNo: '' });
        });
    }
    return ids.map((id: string) => known.get(id)).filter((station: SubwayStation | undefined): station is SubwayStation => Boolean(station));
};

/** "서울 2호선" → "2호선"(칩에서는 노선만 짧게), 그 밖("신분당선")은 그대로 */
export const shortLineName = (lineNo: string): string => {
    const match: RegExpMatchArray | null = lineNo.match(/^\S+\s+(\S*호선)$/);
    return match ? match[1] : lineNo;
};

/** 고른 역 칩 글자 — "강남 · 2호선" */
export const stationPickLabel = (station: SubwayStation): string =>
    station.lineNo ? `${station.name} · ${shortLineName(station.lineNo)}` : station.name;

/** 칩 줄 역 칩 글자 — 없으면 "지하철역", 1곳 "강남", 2곳 "강남 · 역삼", 더 많으면 "강남 외 2" (같은 이름은 한 번) */
export const stationChipLabel = (ids: string[]): string => {
    if (ids.length === 0) return '지하철역';
    const names: string[] = Array.from(new Set(ids.map((id: string) => known.get(id)?.name ?? '역')));
    if (names.length === 1) return ids.length === 1 ? names[0] : `${names[0]} 외 ${ids.length - 1}`;
    if (names.length === 2 && ids.length === 2) return `${names[0]} · ${names[1]}`;
    return `${names[0]} 외 ${ids.length - 1}`;
};
